import React, { useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';

import Modal from '../../modules/Modal';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { findSearchValue } from '../../../helpers';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';

import { updateNotificationTrigger, updateSecondaySelectedClips } from '../../../store/sharedStore/sharedStoreSlice';
import styles from './DeleteTimelineClipModal.module.scss';
import { getClipCommentsByClipId, removeClipForTopic } from '../../../api';
import { TTimelineSession } from '..';
import { updateSortedSelectedClips } from '../../../store/videos/videoSlice';
import { useHotkeys } from 'react-hotkeys-hook';

type TDeleteTimelineClipModal = {
  setSession: React.Dispatch<React.SetStateAction<TTimelineSession[]>>;
  switchModal: () => void;
  open: boolean;
};

const DeleteTimelineClipModal: React.FC<TDeleteTimelineClipModal> = ({ setSession, switchModal, open }) => {
  const [loading, setLoading] = useState(false);
  const notification = useSnackbar();
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const { secondarySelectedClips } = useAppSelector((state) => state.sharedStore);
  const dispatch = useAppDispatch();
  const { sortedSelectedClips } = useAppSelector((state) => state.videoStore);

  const onClose = () => {
    dispatch(updateSecondaySelectedClips([]));
    switchModal();
  };

  const deleteClip = async () => {
    const { id, color, time_end, time_start, qualifiers, notes, topicId } = secondarySelectedClips[0];
    const feedId = findSearchValue('feedId') as string;
    setLoading(true);
    try {
      if (!topicId) throw new Error('Cannot delete a clip without its topic.');
      const { data } = await getClipCommentsByClipId(+id);
      const res = await removeClipForTopic({ ids: [+id] }, +feedId);
      if (res) {
        setSession((prev) => [
          ...prev,
          {
            clip: {
              id,
              time_end: `${time_end}`,
              time_start: `${time_start}`,
              color: color || '#7C7C72',
              topicId: topicId,
              status: 'deleted',
              comments: data[0]?.message ? data : [],
              qualifiers,
              notes,
            },
          },
        ]);
      }
      const selectedClips = sortedSelectedClips;
      if (selectedClips.find((c) => +c.id === +id)) {
        dispatch(updateSortedSelectedClips(selectedClips.filter((c) => +c.id !== +id)));
      }
      dispatch(updateNotificationTrigger(Date.now()));
      onClose();
    } catch (err) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage('Please try again');
      }
    } finally {
      dispatch(updateSecondaySelectedClips([]));
      setLoading(false);
    }
  };

  useHotkeys('Enter', () => {
    if (open && !loading) submitButtonRef?.current?.click();
  });

  return (
    <Modal open={open} onClose={!loading ? onClose : console.log} loading={loading}>
      <div className={styles.modalBody}>
        <div className={styles.modalTitle}>Would you like to delete selected clip{secondarySelectedClips.length > 1 && 's'}?</div>
        <div>
          {secondarySelectedClips.length > 0 && (
            <div className={styles.selectedList}>
              <div>Selected clip{secondarySelectedClips.length > 1 && 's'}:</div>
              {secondarySelectedClips.map((c) => (
                <div key={`${c?.id}_selected_clip`}>
                  {c.topicName} : ({c?.clipIndex! + 1}){' '}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className={styles.form}>
          <div className={styles.actionBtns}>
            <OutlinedButton onClick={deleteClip} kind="default" disabled={loading}>
              <div ref={submitButtonRef}>Yes</div>
            </OutlinedButton>

            <ContainedButton kind="purple" onClick={onClose} disabled={loading}>
              No, cancel
            </ContainedButton>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteTimelineClipModal;
